import { navigationGetHeader } from './api';
import { getCurrentCmsPageId } from './cms';
import { throttle } from './throttle';
import { BREAKPOINTS } from './breakpoints';
import { showToast } from './toast';

export function initHeaderNav() {
    // the header can be reloaded with ajax, we'll have to reinitialize these variables
    let headerNav = null;
    let hamburger = null;
    let menuLinks = null;
    let throttledHandleResize = null;
    const SEARCH_SUGGESTIONS_ENDPOINT = '/api/search/suggestions';
    const SEARCH_SUGGESTIONS_DEBOUNCE_MS = 250;
    const suggestionsCleanupDelay = 120;
    const RESIZE_THROTTLE_MS = 150;

    function initSearchSuggestions(root = document) {
        const forms = root.querySelectorAll('.header-nav__search');

        forms.forEach((form) => {
            if (!form || form.dataset.suggestionsInitialized === 'true') {
                return;
            }

            const input = form.querySelector('.header-nav__search-bar');
            const panel = form.querySelector('[data-suggestions-panel]');
            const productsGroup = panel?.querySelector('[data-suggestions-group="products"]');
            const productsList = panel?.querySelector('[data-suggestions-list="products"]');
            const pagesGroup = panel?.querySelector('[data-suggestions-group="pages"]');
            const pagesList = panel?.querySelector('[data-suggestions-list="pages"]');

            if (!input || !panel || !productsGroup || !productsList || !pagesGroup || !pagesList) {
                return;
            }

            form.dataset.suggestionsInitialized = 'true';

            let debounceTimer = null;
            let activeRequest = null;
                const applyHighlightedText = (element, highlighted, fallback, defaultValue = '') => {
                    if (!element) {
                        return;
                    }

                    const trimmedHighlight = typeof highlighted === 'string' ? highlighted.trim() : '';
                    const trimmedFallback = typeof fallback === 'string' ? fallback.trim() : '';
                    const normalizedDefault = typeof defaultValue === 'string' ? defaultValue : '';

                    if (trimmedHighlight) {
                        element.innerHTML = trimmedHighlight;
                        return;
                    }

                    if (trimmedFallback) {
                        element.textContent = trimmedFallback;
                        return;
                    }

                    element.textContent = normalizedDefault;
                };

                const buildCategoryMetaFragment = (
                    primaryText,
                    secondaryText,
                    primaryHighlight,
                    secondaryHighlight
                ) => {
                    const segments = [];
                    const normalizedPrimaryHighlight = typeof primaryHighlight === 'string' ? primaryHighlight.trim() : '';
                    const normalizedSecondaryHighlight = typeof secondaryHighlight === 'string' ? secondaryHighlight.trim() : '';
                    const normalizedPrimaryText = typeof primaryText === 'string' ? primaryText.trim() : '';
                    const normalizedSecondaryText = typeof secondaryText === 'string' ? secondaryText.trim() : '';

                    if (normalizedPrimaryHighlight) {
                        segments.push({ type: 'html', value: normalizedPrimaryHighlight });
                    } else if (normalizedPrimaryText) {
                        segments.push({ type: 'text', value: normalizedPrimaryText });
                    }

                    if (normalizedSecondaryHighlight) {
                        segments.push({ type: 'html', value: normalizedSecondaryHighlight });
                    } else if (normalizedSecondaryText) {
                        segments.push({ type: 'text', value: normalizedSecondaryText });
                    }

                    if (segments.length === 0) {
                        return null;
                    }

                    const fragment = document.createDocumentFragment();

                    segments.forEach((segment, index) => {
                        if (index > 0) {
                            fragment.appendChild(document.createTextNode(': '));
                        }

                        if (segment.type === 'html') {
                            const wrapper = document.createElement('span');
                            wrapper.innerHTML = segment.value;
                            fragment.appendChild(wrapper);
                        } else {
                            fragment.appendChild(document.createTextNode(segment.value));
                        }
                    });

                    return fragment;
                };

            const clearGroup = (listEl) => {
                if (listEl) {
                    listEl.innerHTML = '';
                }
            };

            const renderEmptyState = (groupEl, listEl, message) => {
                if (!groupEl || !listEl) {
                    return;
                }

                const emptyItem = document.createElement('li');
                emptyItem.className = 'header-nav__search-suggestions-empty';
                emptyItem.textContent = message;
                listEl.appendChild(emptyItem);
                groupEl.hidden = false;
            };

            const clearResults = () => {
                clearGroup(productsList);
                clearGroup(pagesList);
                productsGroup.hidden = true;
                pagesGroup.hidden = true;
                panel.dataset.hasResults = 'false';
            };

            const hidePanel = () => {
                panel.hidden = true;
                panel.setAttribute('aria-hidden', 'true');
            };

            const showPanel = () => {
                panel.hidden = false;
                panel.setAttribute('aria-hidden', 'false');
                panel.dataset.hasResults = 'true';
            };

            const renderProductItems = (items) => {
                clearGroup(productsList);

                if (!Array.isArray(items) || items.length === 0) {
                    renderEmptyState(productsGroup, productsList, 'No product results');
                    return;
                }

                items.forEach((item) => {
                    const li = document.createElement('li');
                    li.className = 'header-nav__search-suggestions-item';

                    const link = document.createElement('a');
                    link.className = 'header-nav__search-suggestions-link header-nav__search-suggestions-link--product';
                    if (item?.url) {
                        link.href = item.url;
                    } else {
                        link.href = '#';
                        link.setAttribute('aria-disabled', 'true');
                        link.addEventListener('click', (ev) => ev.preventDefault());
                    }

                    const copy = document.createElement('span');
                    copy.className = 'header-nav__search-suggestions-copy';

                    const title = document.createElement('span');
                    title.className = 'header-nav__search-suggestions-title';

                    applyHighlightedText(
                        title,
                        item?.highlightedTitle,
                        item?.title,
                        'Untitled'
                    );

                    copy.appendChild(title);

                    if (item?.imageUrl && item.imageUrl.trim()) {
                        const img = document.createElement('img');
                        img.className = 'header-nav__search-suggestions-thumb';
                        img.src = item.imageUrl.trim();
                        img.alt = item?.title?.trim()
                            ? `Product image for ${item.title.trim()}`
                            : 'Product image';
                        link.appendChild(img);
                    }

                    const primaryCategoryName = item?.primaryCategoryName?.trim();
                    const secondaryCategoryName = item?.secondaryCategoryName?.trim();
                    const metaFragment = buildCategoryMetaFragment(
                        primaryCategoryName,
                        secondaryCategoryName,
                        item?.highlightedPrimaryCategoryName,
                        item?.highlightedSecondaryCategoryName
                    );

                    if (metaFragment) {
                        const meta = document.createElement('span');
                        meta.className = 'header-nav__search-suggestions-meta';
                        meta.appendChild(metaFragment);
                        copy.appendChild(meta);
                    }

                    link.appendChild(copy);

                    li.appendChild(link);
                    productsList.appendChild(li);
                });

                productsGroup.hidden = false;
            };

            const renderPageItems = (items) => {
                clearGroup(pagesList);

                if (!Array.isArray(items) || items.length === 0) {
                    renderEmptyState(pagesGroup, pagesList, 'No page results');
                    return;
                }

                items.forEach((item) => {
                    const li = document.createElement('li');
                    li.className = 'header-nav__search-suggestions-item';

                    const link = document.createElement('a');
                    link.className = 'header-nav__search-suggestions-link header-nav__search-suggestions-link--page';
                    if (item?.url) {
                        link.href = item.url;

                        if (item?.contentType === 'Resource' && item?.isPdf) {
                            link.target = '_blank';
                            link.rel = 'noopener noreferrer';
                        }
                    } else {
                        link.href = '#';
                        link.setAttribute('aria-disabled', 'true');
                        link.addEventListener('click', (ev) => ev.preventDefault());
                    }

                    const copy = document.createElement('span');
                    copy.className = 'header-nav__search-suggestions-copy';

                    const title = document.createElement('span');
                    title.className = 'header-nav__search-suggestions-title';

                    applyHighlightedText(
                        title,
                        item?.highlightedTitle,
                        item?.title,
                        'Untitled'
                    );

                    copy.appendChild(title);
                    link.appendChild(copy);

                    if (item?.contentType === 'Resource' && item?.isPdf) {
                        const indicator = document.createElement('span');
                        indicator.className = 'header-nav__search-suggestions-indicator';
                        indicator.textContent = 'PDF';
                        link.appendChild(indicator);
                    }
                    li.appendChild(link);
                    pagesList.appendChild(li);
                });

                pagesGroup.hidden = false;
            };

            const handleResults = (data) => {
                const products = Array.isArray(data?.products) ? data.products : [];
                const pages = Array.isArray(data?.pages) ? data.pages : [];

                renderProductItems(products);
                renderPageItems(pages);

                if (products.length === 0 && pages.length === 0) {
                    hidePanel();
                    panel.dataset.hasResults = 'false';
                    return;
                }

                showPanel();
            };

            const fetchSuggestions = async (term) => {
                if (activeRequest) {
                    activeRequest.abort();
                }

                const controller = new AbortController();
                activeRequest = controller;

                try {
                    const params = new URLSearchParams({ term });
                    const response = await fetch(`${SEARCH_SUGGESTIONS_ENDPOINT}?${params.toString()}`, {
                        signal: controller.signal,
                        headers: {
                            Accept: 'application/json'
                        }
                    });

                    if (!response.ok) {
                        throw new Error(`Suggestions request failed with status ${response.status}`);
                    }

                    const payload = await response.json();
                    handleResults(payload);
                    activeRequest = null;
                } catch (err) {
                    if (err?.name === 'AbortError') {
                        activeRequest = null;
                        return;
                    }

                    console.error('Failed to load search suggestions', err);
                    clearResults();
                    hidePanel();
                    activeRequest = null;
                }
            };

            const debouncedFetch = (term) => {
                if (debounceTimer) {
                    clearTimeout(debounceTimer);
                }

                debounceTimer = setTimeout(() => {
                    fetchSuggestions(term);
                }, SEARCH_SUGGESTIONS_DEBOUNCE_MS);
            };

            const abortActiveRequest = () => {
                if (debounceTimer) {
                    clearTimeout(debounceTimer);
                    debounceTimer = null;
                }

                if (activeRequest) {
                    activeRequest.abort();
                    activeRequest = null;
                }
            };

            const handleInput = () => {
                const term = input.value.trim();

                if (!term) {
                    abortActiveRequest();
                    clearResults();
                    hidePanel();
                    return;
                }

                debouncedFetch(term);
            };

            input.addEventListener('input', handleInput);

            input.addEventListener('focus', () => {
                const term = input.value.trim();

                if (term && panel.dataset.hasResults === 'true') {
                    showPanel();
                }
            });

            input.addEventListener('keydown', (event) => {
                if (event.key === 'Escape') {
                    hidePanel();
                    abortActiveRequest();
                    clearResults();
                    input.blur();
                }
            });

            form.addEventListener('submit', () => {
                abortActiveRequest();
                hidePanel();
            });

            form.addEventListener('focusout', () => {
                window.setTimeout(() => {
                    if (!form.contains(document.activeElement)) {
                        hidePanel();
                    }
                }, suggestionsCleanupDelay);
            });

            clearResults();
            hidePanel();
        });
    }
    
    function toggleMobileMenu() {
        const searchWrapper = document.querySelector('.header-nav__search-wrapper--mobile');
        const loginMobile = document.querySelector('.header-nav__login--mobile');
        const menuWrapper = document.querySelector('.header-nav__menu-wrapper');
        
        // Check if menu is currently open (will be closed after toggle)
        const isCurrentlyOpen = headerNav?.classList.contains('header-nav--open');
        
        headerNav?.classList.toggle('header-nav--open');
        hamburger?.classList.toggle('is-active');
        searchWrapper?.classList.toggle('visible');
        loginMobile?.classList.toggle('visible');
        
        // Reset menu state when closing
        if (isCurrentlyOpen) {
            // Remove active state from all menu items
            menuWrapper?.querySelectorAll('.header-nav__menu--active').forEach(item => {
                item.classList.remove('header-nav__menu--active');
            });
            
            // Close all open submenus
            menuWrapper?.querySelectorAll('.header-nav__menu-standard.open, .header-nav__menu-mega.open').forEach(submenu => {
                submenu.classList.remove('open');
            });
            
            // Close all mega menu accordion items
            document.querySelectorAll('.header-nav__menu-title.open').forEach(title => {
                title.classList.remove('open');
            });
            
            // Restore original menu order
            restoreOriginalOrder();
            syncMenuOpenState();
        }

    }

    function initOriginalOrder() {
        const menuWrapper = document.querySelector('.header-nav__menu-wrapper');
        if (!menuWrapper) return;

        const items = Array.from(menuWrapper.querySelectorAll('.header-nav__menu'));
        items.forEach((el, idx) => {
            el.dataset.originalIndex = idx;
        });
    }

    function restoreOriginalOrder() {
        const menuWrapper = document.querySelector('.header-nav__menu-wrapper');
        if (!menuWrapper) return;

        const items = Array.from(menuWrapper.querySelectorAll('.header-nav__menu'));

        items.sort((a, b) => {
            return Number(a.dataset.originalIndex) - Number(b.dataset.originalIndex);
        });

        items.forEach(item => menuWrapper.appendChild(item));
    }

    function syncMenuOpenState() {
        const menuWrapper = document.querySelector('.header-nav__menu-wrapper');
        if (!menuWrapper) return;

        const hasOpenSubmenu = Boolean(
            menuWrapper.querySelector('.header-nav__menu-standard.open, .header-nav__menu-mega.open')
        );

        menuWrapper.classList.toggle('menu-open', hasOpenSubmenu);
    }

    function handleSubmenuToggle(e) {
        const parentMenuItem = e.target.closest('.header-nav__menu');
        const menuWrapper = document.querySelector('.header-nav__menu-wrapper');
        const submenu = parentMenuItem?.querySelector('.header-nav__menu-standard, .header-nav__menu-mega');

        if (submenu && window.innerWidth < BREAKPOINTS.LARGE) {
            e.preventDefault();

            const isOpen = submenu.classList.contains('open');

            if (!isOpen) {
                menuWrapper?.querySelectorAll('.header-nav__menu--active').forEach(item => {
                    item.classList.remove('header-nav__menu--active');
                });

                if (menuWrapper && parentMenuItem) {
                    menuWrapper.prepend(parentMenuItem);
                    parentMenuItem.classList.add('header-nav__menu--active');
                }

                menuWrapper?.querySelectorAll('.header-nav__menu-standard.open, .header-nav__menu-mega.open').forEach(openSubmenu => {
                    if (openSubmenu !== submenu) {
                        openSubmenu.classList.remove('open');
                    }
                });

                submenu.classList.add('open');
            } else {
                submenu.classList.remove('open');
                parentMenuItem?.classList.remove('header-nav__menu--active');
                restoreOriginalOrder();
            }

            syncMenuOpenState();
        }
    }

    function handleResize() {
        const searchWrapper = document.querySelector('.header-nav__search-wrapper--mobile');
        const menuWrapper = document.querySelector('.header-nav__menu-wrapper');

        if (window.innerWidth >= BREAKPOINTS.LARGE) {
            headerNav?.classList.remove('header-nav--open');
            hamburger?.classList.remove('is-active');
            searchWrapper?.classList.remove('visible');
            document.querySelector('.header-nav__login--mobile')?.classList.remove('visible');
            
            // Reset menu state when switching to desktop
            menuWrapper?.querySelectorAll('.header-nav__menu--active').forEach(item => {
                item.classList.remove('header-nav__menu--active');
            });
            
            menuWrapper?.querySelectorAll('.header-nav__menu-standard.open, .header-nav__menu-mega.open').forEach(submenu => {
                submenu.classList.remove('open');
            });
            
            document.querySelectorAll('.header-nav__menu-title.open').forEach(title => {
                title.classList.remove('open');
            });
            
            restoreOriginalOrder();
            syncMenuOpenState();
        }
    }
    
    function initMegaMenuAccordions() {
        const megaMenuTitles = document.querySelectorAll('.header-nav__menu-title');

        megaMenuTitles.forEach(title => {
            title.addEventListener('click', (e) => {
                if (window.innerWidth < BREAKPOINTS.LARGE) {
                    const submenu = title.nextElementSibling;

                    if (submenu && submenu.tagName === 'UL') {
                        const isOpen = submenu.classList.contains('open');

                        submenu.classList.toggle('open');
                        title.classList.toggle('open');

                        megaMenuTitles.forEach(otherTitle => {
                            const otherSubmenu = otherTitle.nextElementSibling;
                            if (otherTitle !== title && otherSubmenu?.classList.contains('open')) {
                                otherSubmenu.classList.remove('open');
                                otherTitle.classList.remove('open');
                            }
                        });

                        e.preventDefault();
                    }
                }
            });
        });
    }
    
    function init() {
        headerNav = document.querySelector('.header-nav');
        hamburger = document.querySelector('.header-nav__hamburger');
        menuLinks = document.querySelectorAll('.header-nav__menu > a');
        
        initOriginalOrder();

        if (hamburger) {
            hamburger.addEventListener('click', toggleMobileMenu);
        }

        menuLinks.forEach(link => {
            link.addEventListener('click', (e) => {
                handleSubmenuToggle(e);
            });
        });

        throttledHandleResize = throttle(handleResize, RESIZE_THROTTLE_MS);
        window.addEventListener('resize', throttledHandleResize);

        initMegaMenuAccordions();
        initSearchSuggestions(headerNav ?? document);
        initMobileProfileClick();
    }
    
    function initMobileProfileClick() {
        const mobileProfileSummary = document.querySelector('.header-nav__login--mobile .profile-summary');
        
        if (mobileProfileSummary) {
            mobileProfileSummary.addEventListener('click', (e) => {
                const accountUrl = mobileProfileSummary.dataset.mobileAccountUrl;
                if (accountUrl) {
                    window.location.href = accountUrl;
                }
            });
        }
    }
    
    function reloadHeader() {
        navigationGetHeader(getCurrentCmsPageId())
            .then(header => {
                // clean up resize handler and replace the original DOM elements,
                // the event handler, and element references will be reinitialized by init()
                if (throttledHandleResize) {
                    window.removeEventListener('resize', throttledHandleResize);
                }
                headerNav.outerHTML = header;
                init();
                
                // Dispatch event to signal header reload is complete
                document.dispatchEvent(new Event('mm.headerReloaded'));
            })
            .catch(err => {
                console.error('Failed to reload header', err);
                showToast('Sorry, there was a problem reloading the menu. Please refresh the page or try again later.', 'error');
            });
    }

    document.addEventListener('DOMContentLoaded', init);
    document.addEventListener('mm.signedIn', reloadHeader);
}
